import { pickUtmWithFallback } from 'stash/utils/attribution/pickUtmWithFallback';
import { getSuperCookie } from 'stash/utils/superCookie';
import { CLIENT_IDS, REDIRECT_URLS } from 'stash/constants/authParams';

export const isEmpty = (obj) => {
	return !obj || !Object.keys(obj).length;
};

// Build the base query string for an auth redirect. Returns the `key=value&...`
// fragment without a leading `?` — callers append to their endpoint path.
//
// Why this is a hybrid of raw interpolation + encodeURIComponent and NOT pure
// URLSearchParams:
//   - Controlled OAuth params (`client_id`, `redirect_uri`, `state`,
//     `code_challenge`) are server-generated constants or tokens that never
//     contain `&`, `=`, `#`, or `?`, so raw interpolation cannot corrupt the
//     query string. More importantly, URLSearchParams would percent-encode the
//     `://` and `/` in `redirect_uri` (e.g. `https%3A%2F%2Fapp.stash.com%2F
//     authorize`). OAuth servers commonly do exact-string comparison on
//     `redirect_uri` against the registered value, so changing the raw shape
//     is a real risk on a high-stakes auth path. Preserve pre-refactor bytes.
//   - Attacker-controllable values (UTM fields, promo codes) go through
//     `encodeURIComponent` so values like `vendor&foo=bar` can't corrupt the
//     query string — the bug class fixed in PR #3154.
const buildAuthQueryString = ({
	clientId,
	redirectUrl,
	stateParam,
	codeChallenge,
	version,
	extraParams = {},
}) => {
	let queryString =
		`client_id=${clientId}` +
		`&redirect_uri=${redirectUrl}` +
		`&state=${stateParam}` +
		`&response_type=code` +
		`&code_challenge=${codeChallenge}` +
		`&code_challenge_method=S256`;

	for (const [key, value] of Object.entries(extraParams)) {
		if (value) {
			queryString += `&${key}=${encodeURIComponent(value)}`;
		}
	}

	queryString += `&client_version=${encodeURIComponent(`web-reg/${version}`)}`;

	return queryString;
};

export const buildAuthRedirect = ({
	env,
	stateParam,
	codeChallenge,
	stash_code,
	stash_code_v2,
	baseUrl,
	version,
	segmentId,
	// Default keeps `origin` optional for callers that have no intro attribution.
	origin = /** @type {string | undefined} */ (undefined),
}) => {
	const clientId = CLIENT_IDS[env];
	const redirectUrl = REDIRECT_URLS[env];

	// Read UTMs from the super cookie first (canonical source); fall back to
	// individual cookies during the transition period. `pickUtmWithFallback`
	// emits a Datadog RUM action every time the fallback actually produces a
	// value, giving us visibility into super-cookie coverage gaps.
	const superCookie = getSuperCookie();
	const callsite = 'buildAuthRedirect';
	const extraParams = {
		stash_code_v2: stash_code_v2 || undefined,
		stash_code: stash_code_v2 ? undefined : stash_code || undefined,
		utm_source: pickUtmWithFallback(superCookie, 'utm_source', callsite),
		utm_medium: pickUtmWithFallback(superCookie, 'utm_medium', callsite),
		utm_campaign: pickUtmWithFallback(superCookie, 'utm_campaign', callsite),
		utm_content: pickUtmWithFallback(superCookie, 'utm_content', callsite),
		utm_term: pickUtmWithFallback(superCookie, 'utm_term', callsite),
		utm_provider: pickUtmWithFallback(superCookie, 'utm_provider', callsite),
	};

	// stash-auth consumes `segment_id` before UserSignup_Viewed.
	if (typeof segmentId === 'string') {
		const normalizedSegmentId = segmentId.trim();
		if (normalizedSegmentId) {
			extraParams.segment_id = normalizedSegmentId;
		}
	}

	// Auth0 only exposes allowlisted `ext-` params to the signup screen.
	// `origin` stays for the signup app that reads the redirect query directly.
	if (typeof origin === 'string' && /^[a-z0-9_]+$/.test(origin)) {
		extraParams.origin = origin;
		extraParams['ext-origin'] = origin;
	}

	const queryString = buildAuthQueryString({
		clientId,
		redirectUrl,
		stateParam,
		codeChallenge,
		version,
		extraParams,
	});

	return `${baseUrl}/sign_up?${queryString}`;
};

export const buildAuthRedirectLogin = ({
	env,
	stateParam,
	codeChallenge,
	baseUrl,
	version,
	origin = /** @type {string | undefined} */ (undefined),
}) => {
	const clientId = CLIENT_IDS[env];
	const redirectUrl = REDIRECT_URLS[env];
	const extraParams = {};

	if (typeof origin === 'string' && /^[a-z0-9_]+$/.test(origin)) {
		extraParams.origin = origin;
		extraParams['ext-origin'] = origin;
	}

	const queryString = buildAuthQueryString({
		clientId,
		redirectUrl,
		stateParam,
		codeChallenge,
		version,
		extraParams,
	});

	return `${baseUrl}/authorize?${queryString}`;
};
